// hooks/useCallNotifications.js
// ─────────────────────────────────────────────────────────────
// Connects the logged-in user to their personal presence channel
// (/api/calling/presence) and dispatches call events to handlers.
// Adopted by CallProvider so calls arrive on EVERY authenticated page.
//
// Handlers are read through a ref, so the long-lived socket never
// captures a stale closure. `enabled` gates the connection (off on
// auth pages / superadmin / while logged out) and auto-reconnects.
//
// Dispatched event types:
//   incoming_call · call_accepted · call_cancelled · call_declined ·
//   call_quick_reply · call_recording_notice · user_free
// ─────────────────────────────────────────────────────────────

import { useEffect, useRef } from "react";

const WS_BASE = (process.env.REACT_APP_API_BASE || "http://localhost:8000")
  .replace("https://", "wss://")
  .replace("http://", "ws://");

export function useCallNotifications({
  enabled = true,
  onIncomingCall,
  onCallAccepted,
  onCallCancelled,
  onCallDeclined,
  onCallQuickReply,
  onRecordingNotice,
  onUserFree,
} = {}) {
  const handlersRef = useRef({});
  handlersRef.current = {
    onIncomingCall,
    onCallAccepted,
    onCallCancelled,
    onCallDeclined,
    onCallQuickReply,
    onRecordingNotice,
    onUserFree,
  };

  const wsRef      = useRef(null);
  const pingRef    = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    mountedRef.current = true;

    const connect = () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      const ws = new WebSocket(`${WS_BASE}/api/calling/presence?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        pingRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
      };

      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch (_) { return; }
        const h = handlersRef.current;
        switch (msg.type) {
          case "incoming_call":         h.onIncomingCall?.(msg);    break;
          case "call_accepted":         h.onCallAccepted?.(msg);    break;
          case "call_cancelled":        h.onCallCancelled?.(msg);   break;
          case "call_declined":         h.onCallDeclined?.(msg);    break;
          case "call_quick_reply":      h.onCallQuickReply?.(msg);  break;
          case "call_recording_notice": h.onRecordingNotice?.(msg); break;
          case "user_free":             h.onUserFree?.(msg);        break;
          default: break;
        }
      };

      ws.onclose = () => {
        clearInterval(pingRef.current);
        if (mountedRef.current) setTimeout(connect, 3000); // reconnect while enabled
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      mountedRef.current = false;
      clearInterval(pingRef.current);
      wsRef.current?.close();
    };
  }, [enabled]);
}
